import type {
  DuplicatedValue,
  OmittedValue,
  PreviewIssueTag,
} from "@/apis/preview/v2/types";
import {
  getPreviewIssueKey,
  getPreviewIssueMetaLabel,
} from "./preview-issue-utils";

const ISSUE_HEADER_CELL_CLASS =
  "border border-line-normal bg-background-neutral-neutral px-3 py-2 text-left label-1-normal-medium text-label-normal whitespace-nowrap";
const ISSUE_CELL_CLASS =
  "border border-line-normal bg-white px-3 py-2 label-1-normal-regular whitespace-nowrap";
const ISSUE_VALUE_CELL_CLASS = `${ISSUE_CELL_CLASS} text-right text-primary`;
const ISSUE_TEXT_CELL_CLASS = `${ISSUE_CELL_CLASS} text-primary`;
const ISSUE_HIDDEN_VALUE_CLASS = "text-red-500";

type IssueValueCellData = {
  key: string;
  value: string;
  cellAddress: string;
  cellType: string;
  isVisible?: boolean;
};

type IssueTableRow = {
  key: string;
  tableName: string;
  roleKey: string | null;
  values: IssueValueCellData[];
  lineItem: PreviewIssueTag | null;
  members: PreviewIssueTag[];
  begend: string;
  accumulatedType: string | null;
};

const getTagDisplayLabel = (tag: PreviewIssueTag) => tag.label ?? tag.id;

const createOmittedIssueRow = (
  omittedValue: OmittedValue,
  index: number,
): IssueTableRow => ({
  key: getPreviewIssueKey(
    omittedValue,
    index,
    "omitted",
    omittedValue.cell.cellAddress,
  ),
  tableName: omittedValue.tableName,
  roleKey: omittedValue.roleKey,
  values: [
    {
      key: `${omittedValue.cell.cellAddress}-${index}`,
      value: omittedValue.cell.value,
      cellAddress: omittedValue.cell.cellAddress,
      cellType: omittedValue.cell.cellType,
    },
  ],
  lineItem: omittedValue.lineItem,
  members: omittedValue.members,
  begend: omittedValue.begend,
  accumulatedType: omittedValue.accumulatedType,
});

const createDuplicatedIssueRow = (
  duplicatedValue: DuplicatedValue,
  index: number,
): IssueTableRow => ({
  key: getPreviewIssueKey(
    duplicatedValue,
    index,
    "duplicated",
    duplicatedValue.values.map((value) => value.cellAddress).join("|"),
  ),
  tableName: duplicatedValue.tableName,
  roleKey: duplicatedValue.roleKey,
  values: duplicatedValue.values.map((value, valueIndex) => ({
    key: `${duplicatedValue.cellTagKey}-${value.cellAddress}-${valueIndex}`,
    value: value.value,
    cellAddress: value.cellAddress,
    cellType: value.cellType,
    isVisible: value.isVisible,
  })),
  lineItem: duplicatedValue.lineItem,
  members: duplicatedValue.members,
  begend: duplicatedValue.begend,
  accumulatedType: duplicatedValue.accumulatedType,
});

const IssueTableNameCell = ({
  tableName,
  roleKey,
}: {
  tableName: string;
  roleKey: string | null;
}) => (
  <td className={ISSUE_TEXT_CELL_CLASS}>
    <div className="flex max-w-[260px] flex-col gap-0.5">
      <span className="truncate">{tableName || roleKey || "-"}</span>
      {roleKey && (
        <span className="caption-2-medium text-label-assistive truncate">
          {roleKey}
        </span>
      )}
    </div>
  </td>
);

const EmptyIssueValue = () => <span className="text-label-assistive">-</span>;

const IssueValueCell = ({ values }: { values: IssueValueCellData[] }) => (
  <td className={ISSUE_VALUE_CELL_CLASS}>
    <div className="flex flex-col gap-1.5">
      {values.length > 0 ? (
        values.map((value) => (
          <div key={value.key} className="flex flex-col gap-0.5">
            <span
              className={
                value.isVisible === false ? ISSUE_HIDDEN_VALUE_CLASS : undefined
              }
            >
              {value.value}
            </span>
            <div className="flex items-center justify-end gap-1.5">
              <span className="caption-2-medium text-label-alternative">
                {value.cellAddress}
              </span>
              {value.cellType && (
                <span className="caption-2-medium text-label-assistive">
                  {value.cellType}
                </span>
              )}
            </div>
          </div>
        ))
      ) : (
        <EmptyIssueValue />
      )}
    </div>
  </td>
);

const IssueLineItemCell = ({ row }: { row: IssueTableRow }) => {
  const metaLabel = getPreviewIssueMetaLabel(row);

  return (
    <td className={ISSUE_TEXT_CELL_CLASS}>
      {row.lineItem ? (
        <div className="flex flex-col gap-0.5">
          <span>{getTagDisplayLabel(row.lineItem)}</span>
          {metaLabel && (
            <span className="caption-2-medium text-label-assistive">
              {metaLabel}
            </span>
          )}
        </div>
      ) : (
        <EmptyIssueValue />
      )}
    </td>
  );
};

const IssueMembersCell = ({ row }: { row: IssueTableRow }) => (
  <td className={ISSUE_TEXT_CELL_CLASS}>
    {row.members.length > 0 ? (
      <div className="flex flex-col gap-1">
        {row.members.map((member, memberIndex) => (
          <span
            key={`${row.key}-member-${memberIndex}`}
            className="whitespace-nowrap"
          >
            {getTagDisplayLabel(member)}
          </span>
        ))}
      </div>
    ) : (
      <EmptyIssueValue />
    )}
  </td>
);

const IssueValuesTable = ({
  title,
  rows,
}: {
  title: string;
  rows: IssueTableRow[];
}) => {
  if (rows.length === 0) return null;

  return (
    <div className="flex flex-col p-[16px] items-start gap-[10px] bg-white rounded-2xl max-w-full">
      <span className="headline-2-semibold text-primary">{title}</span>
      <div className="max-w-full overflow-x-auto">
        <table className="border-collapse border border-line-normal w-auto">
          <thead>
            <tr>
              <th className={ISSUE_HEADER_CELL_CLASS}>표</th>
              <th className={ISSUE_HEADER_CELL_CLASS}>값</th>
              <th className={ISSUE_HEADER_CELL_CLASS}>라인아이템</th>
              <th className={ISSUE_HEADER_CELL_CLASS}>멤버</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <IssueTableNameCell
                  tableName={row.tableName}
                  roleKey={row.roleKey}
                />
                <IssueValueCell values={row.values} />
                <IssueLineItemCell row={row} />
                <IssueMembersCell row={row} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const PreviewIssuesSection = ({
  omittedValues,
  duplicatedValues,
}: {
  omittedValues: OmittedValue[];
  duplicatedValues: DuplicatedValue[];
}) => {
  if (omittedValues.length === 0 && duplicatedValues.length === 0) return null;

  const omittedRows = omittedValues.map(createOmittedIssueRow);
  const duplicatedRows = duplicatedValues.map(createDuplicatedIssueRow);

  return (
    <div className="flex flex-col items-start gap-[24px] max-w-full">
      <IssueValuesTable title="태깅했는데 안 보이게 된 값" rows={omittedRows} />
      <IssueValuesTable title="중복 태깅된 값" rows={duplicatedRows} />
    </div>
  );
};
